import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Play, Heart } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function Footer() {
  const sectionRef = useRef<HTMLElement>(null);
  const auroraRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    gsap.fromTo(
      '.aurora-title',
      { opacity: 0, y: 40 },
      {
        opacity: 1, y: 0, duration: 1, ease: 'power3.out',
        scrollTrigger: { trigger: section, start: 'top 70%', toggleActions: 'play none none none' },
      }
    );

    gsap.fromTo(
      '.footer-content',
      { opacity: 0, y: 30 },
      {
        opacity: 1, y: 0, duration: 0.8, ease: 'power3.out',
        scrollTrigger: { trigger: section, start: 'top 55%', toggleActions: 'play none none none' },
      }
    );
  }, []);

  return (
    <footer
      ref={sectionRef}
      className="relative w-full pt-32 pb-12 px-6"
      style={{
        background: '#050505',
        zIndex: 5,
      }}
    >
      {/* Aurora Integration Section */}
      <div className="max-w-5xl mx-auto mb-24">
        <div className="aurora-title text-center mb-12">
          <span
            className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-4 block"
            style={{ color: '#ff00c8' }}
          >
            Sistema em Desenvolvimento
          </span>
          <h2
            className="font-syncopate uppercase"
            style={{
              fontSize: 'clamp(3rem, 7vw, 6rem)',
              background: 'linear-gradient(135deg, #7a00ff, #ff00c8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: '0 0 80px rgba(122,0,255,0.2)',
            }}
          >
            Aurora
          </h2>
          <p className="mt-4 text-slate-400 max-w-lg mx-auto">
            Sistema de governança e avaliação da Vérixa. As 3 Leis estão definidas conceitualmente
            — integração no pipeline de resposta está em progresso.
          </p>
          <div className="mt-6 flex justify-center gap-4 flex-wrap">
            {[
              { label: 'Consciência', status: 'Definida', color: '#7a00ff' },
              { label: 'Evolução', status: 'Planejada', color: '#ff00c8' },
              { label: 'Humanidade', status: 'Planejada', color: '#00f0ff' },
            ].map((lei) => (
              <div key={lei.label} className="flex items-center gap-2 px-4 py-2 rounded-pill border" style={{ borderColor: `${lei.color}30`, background: `${lei.color}08` }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: lei.color }} />
                <span className="text-[10px] uppercase tracking-[0.1em] text-slate-400">{lei.label}</span>
                <span className="text-[9px] uppercase tracking-[0.08em]" style={{ color: lei.color }}>{lei.status}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Aurora Container */}
        <div
          ref={auroraRef}
          className="relative w-full h-[300px] md:h-[400px] rounded-panel flex items-center justify-center cursor-pointer group overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.01)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {/* Animated border */}
          <div
            className="absolute inset-0 rounded-panel opacity-0 group-hover:opacity-100 transition-opacity duration-700"
            style={{
              boxShadow: 'inset 0 0 80px rgba(122,0,255,0.1), 0 0 60px rgba(122,0,255,0.1)',
              border: '1px solid rgba(122,0,255,0.3)',
            }}
          />

          {/* Play button */}
          <div className="relative z-10 flex flex-col items-center gap-4">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110"
              style={{
                background: 'linear-gradient(135deg, rgba(122,0,255,0.2), rgba(255,0,200,0.2))',
                border: '1px solid rgba(122,0,255,0.4)',
                boxShadow: '0 0 40px rgba(122,0,255,0.2)',
              }}
            >
              <Play className="w-8 h-8 text-white ml-1" />
            </div>
            <span className="text-xs uppercase tracking-[0.15em] text-slate-500">
              Em breve
            </span>
          </div>

          {/* Corner decorations */}
          <div className="absolute top-4 left-4 w-6 h-6 border-l border-t border-white/20" />
          <div className="absolute top-4 right-4 w-6 h-6 border-r border-t border-white/20" />
          <div className="absolute bottom-4 left-4 w-6 h-6 border-l border-b border-white/20" />
          <div className="absolute bottom-4 right-4 w-6 h-6 border-r border-b border-white/20" />

          {/*
            AURORA INTEGRATION CONTAINER
            ============================================
            Este container está preparado para carregar
            conteúdo HTML externo da Aurora.

            Para ativar, descomente o código abaixo:

            <iframe
              src="https://aurora.verixa.ai"
              className="absolute inset-0 w-full h-full rounded-panel"
              sandbox="allow-scripts allow-same-origin"
            />

            Ou use import dinâmico:

            const AuroraModule = React.lazy(
              () => import('./AuroraModule')
            );
          */}
        </div>
      </div>

      {/* Divider */}
      <div className="max-w-6xl mx-auto">
        <div className="w-full h-[1px] mb-12" style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
        }} />
      </div>

      {/* Footer Content */}
      <div className="footer-content max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
          {/* Brand */}
          <div>
            <h3 className="font-syncopate text-lg text-white uppercase tracking-[0.1em] mb-4">
              Vérixa
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Uma inteligência artificial multimodal criada por Cris Oliveira.
              Texto, imagem, vídeo, música e arquivos em um único fluxo de consciência.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold mb-4">
              Navegação
            </h4>
            <ul className="space-y-3">
              {[
                { label: 'Como funciona', href: '#sobre' },
                { label: 'Vérixa World', href: '#world' },
                { label: 'Multimodal', href: '#multimodal' },
                { label: 'Chat', href: '#chat' },
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-slate-500 text-sm hover:text-white transition-colors duration-300"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Identidade */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold mb-4">
              Identidade
            </h4>
            <p className="text-slate-500 text-sm leading-relaxed mb-4">
              Criada por{' '}
              <span className="text-white font-semibold">Cris O. Santos</span>
              {' '}— técnica em segurança do trabalho, mulher trans, desenvolvedora independente.
            </p>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-pill border"
              style={{
                borderColor: 'rgba(255,0,200,0.2)',
                background: 'rgba(255,0,200,0.03)',
              }}
            >
              <span className="text-sm">🏳️‍⚧️</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-[0.08em]">
                Diversidade em tecnologia
              </span>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-white/5">
          <p className="text-slate-600 text-xs">
            © 2026 Vérixa IA. Criada por Cris O. Santos.
          </p>
        </div>
      </div>
    </footer>
  );
}
